export const EMBED_SCRIPT_ID = "__singular_chatbase_embed";
const DEFAULT_SCRIPT_URL = "https://www.chatbase.co/embed.min.js";

export interface InjectScriptOptions {
  botId: string;
  domain?: string;
  scriptUrl?: string;
}

/**
 * Inject the Chatbase embed `<script>` tag into `<body>`.
 *
 * Sets `window.embeddedChatbotConfig` and injects a `<script>` tag with the
 * `chatbotId` and `domain` attributes that the Chatbase embed expects.
 *
 * - Prevents duplicate injection by tracking a deterministic element id.
 * - Rejects if the script fails to load.
 * - Resolves once the script has loaded (not necessarily fully initialised
 *   — use `waitForReady` for that).
 */
export function injectScript(opts: InjectScriptOptions): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    if (typeof document === "undefined") {
      reject(new Error("Cannot inject Chatbase script outside a browser environment."));
      return;
    }

    if (document.getElementById(EMBED_SCRIPT_ID)) {
      resolve(); // already injected
      return;
    }

    // Set the config global the embed script reads on load
    const domain = opts.domain ?? "www.chatbase.co";
    (window as Window & { embeddedChatbotConfig?: unknown }).embeddedChatbotConfig = {
      chatbotId: opts.botId,
      domain,
    };

    const script = document.createElement("script");
    script.src = opts.scriptUrl ?? DEFAULT_SCRIPT_URL;
    script.id = EMBED_SCRIPT_ID;
    script.setAttribute("chatbotId", opts.botId);
    script.setAttribute("domain", domain);
    script.defer = true;

    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load Chatbase embed script."));

    document.body.appendChild(script);
  });
}

/**
 * Wait until the Chatbase embed has created `window.chatbase` and its
 * widget DOM.
 *
 * The Chatbase embed lazy-loads its iframe on first interaction, so
 * `getState` won't return `"initialized"` until the widget is opened.
 * This function waits for the embed script to set up `window.chatbase`
 * and render the bubble button, then programmatically opens and closes
 * the widget to force full initialisation.
 *
 * Polls with exponential back-off up to the configured timeout.
 */
export function waitForReady(timeoutMs = 15_000): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    if (typeof window === "undefined") {
      reject(new Error("Not in a browser environment."));
      return;
    }

    const start = Date.now();
    let delay = 100;

    const check = () => {
      // Stage 1: Wait for the embed to set up window.chatbase and the bubble button
      const hasChatbase = window.chatbase && typeof window.chatbase === "function";
      const hasBubble =
        typeof document !== "undefined" && !!document.getElementById("chatbase-bubble-button");

      if (hasChatbase && hasBubble) {
        // Stage 2: Open the widget to force iframe load, then wait for "initialized"
        try {
          window.chatbase!("open");
        } catch {
          // ignore
        }

        const waitInit = () => {
          try {
            if (
              window.chatbase &&
              typeof window.chatbase === "function" &&
              window.chatbase("getState") === "initialized"
            ) {
              // Close the widget now that it's initialised
              try {
                window.chatbase("close");
              } catch {
                // ignore
              }
              resolve();
              return;
            }
          } catch {
            // getState may throw
          }

          if (Date.now() - start > timeoutMs) {
            reject(new Error("Chatbase did not initialise within the timeout period."));
            return;
          }

          setTimeout(waitInit, 200);
        };

        waitInit();
        return;
      }

      if (Date.now() - start > timeoutMs) {
        reject(new Error("Chatbase did not initialise within the timeout period."));
        return;
      }

      delay = Math.min(delay * 1.5, 2000);
      setTimeout(check, delay);
    };

    check();
  });
}

/**
 * Remove the Chatbase embed script and widget iframe from the DOM,
 * and delete `window.chatbase`.
 */
export function removeWidget(): void {
  if (typeof document === "undefined") return;

  // Remove the injected <script> tag
  document.getElementById(EMBED_SCRIPT_ID)?.remove();

  // Remove Chatbase iframes (the widget injects iframes with src containing chatbase.co)
  const iframes = document.querySelectorAll<HTMLIFrameElement>("iframe");
  for (const iframe of iframes) {
    if (iframe.src?.includes("chatbase.co") || iframe.id?.includes("chatbase")) {
      iframe.remove();
    }
  }

  // Remove any Chatbase-injected container divs
  const containers = document.querySelectorAll<HTMLElement>("[id*='chatbase']");
  for (const el of containers) {
    el.remove();
  }

  // Clean up the globals
  /* v8 ignore next — SSR guard unreachable in jsdom */
  if (typeof window !== "undefined") {
    delete (window as Partial<typeof window>).chatbase;
    delete (window as Window & { embeddedChatbotConfig?: unknown }).embeddedChatbotConfig;
  }
}
